import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Select, { StylesConfig, SingleValue } from 'react-select';
import { employeeApi, locationApi, payrollEmploymentTypesApi, getProfileImageUrl } from '../services/api';
import { Employee, Role, CreateEmployeeRequest, Location, Position, PayrollDepartment, PayrollEmploymentType, Schedule, PaymentType, PaymentFrequency, LeavePolicy } from '../types';
import { Plus, Edit, Trash2, Search, User, Filter, X, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Users, Network, Building2, Loader2 } from 'lucide-react';
import BasketSelect from '../components/BasketSelect';
import { useLocationContext } from '../components/Layout';
import { appConfirm } from '../context/SystemDialogContext';

/** Loaded only when user opens reporting hierarchy from a row (code-split). */
const EmployeeHierarchyModal = lazy(() => import('../components/EmployeeHierarchyModal'));

interface FilterCondition {
  id: string;
  field: string;
  value: string | number | boolean | number[] | string[]; // Support arrays for multi-select
  valueFrom?: string | number | null; // For range filters (date/number)
  valueTo?: string | number | null; // For range filters (date/number)
}

type FilterFieldType = 'text' | 'date' | 'select' | 'boolean' | 'number' | 'dateRange' | 'numberRange' | 'multiSelect';

interface FilterFieldOption {
  value: string;
  label: string;
  type: FilterFieldType;
  tab: string;
  options?: Array<{ value: string | number; label: string }>;
}

type EmployeeSelectOption = { value: number; label: string };

const ManagerAssignmentRow: React.FC<{
  location: Location;
  employees: Employee[];
  onManagerChange: (employeeId: number | null) => Promise<void>;
  saving: boolean;
}> = ({ location, employees, onManagerChange, saving }) => {
  const options: EmployeeSelectOption[] = employees.map(e => ({
    value: e.id,
    label: `${e.firstName} ${e.lastName}${e.email ? ` (${e.email})` : ''}`
  }));
  const value: EmployeeSelectOption | null = location.managerId && location.manager
    ? { value: location.manager.id, label: `${location.manager.firstName} ${location.manager.lastName}${location.manager.email ? ` (${location.manager.email})` : ''}` }
    : location.managerId
      ? options.find(o => o.value === location.managerId) ?? null
      : null;

  const selectStyles: StylesConfig<EmployeeSelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      minWidth: 220
    })
  };

  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-neutral-100 last:border-0">
      <span className="font-medium text-neutral-800 flex-shrink-0">{location.name}</span>
      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
        <Select<EmployeeSelectOption>
          value={value}
          options={options}
          onChange={(opt: SingleValue<EmployeeSelectOption>) => {
            onManagerChange(opt?.value ?? null);
          }}
          isClearable
          placeholder="Search managers..."
          styles={selectStyles}
          isDisabled={saving}
          filterOption={(option, input) =>
            option.label.toLowerCase().includes((input || '').toLowerCase())
          }
        />
        {saving && <span className="text-xs text-neutral-500">Saving...</span>}
      </div>
    </div>
  );
};

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId } = useLocationContext();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [payrollEmploymentTypes, setPayrollEmploymentTypes] = useState<PayrollEmploymentType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Active' | 'InActive' | 'All'>('Active');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const listRequestRef = useRef(0);
  const [sortColumn, setSortColumn] = useState<'name' | 'position' | 'location' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showManagerAssignmentModal, setShowManagerAssignmentModal] = useState(false);
  const [locationsWithManagers, setLocationsWithManagers] = useState<Location[]>([]);
  const [managerAssignmentLoading, setManagerAssignmentLoading] = useState(false);
  const [managerAssignmentSaving, setManagerAssignmentSaving] = useState<Record<number, boolean>>({});
  const [showSubLocationsDialog, setShowSubLocationsDialog] = useState(false);
  const [showSubPositionsDialog, setShowSubPositionsDialog] = useState(false);
  const [selectedEmployeeForSubLocations, setSelectedEmployeeForSubLocations] = useState<Employee | null>(null);
  const [selectedEmployeeForSubPositions, setSelectedEmployeeForSubPositions] = useState<Employee | null>(null);
  const [hierarchyEmployeeId, setHierarchyEmployeeId] = useState<number | null>(null);
  const [managerPickerEmployees, setManagerPickerEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<CreateEmployeeRequest>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    pin: '',
    appRoleIds: [],
    isActive: true,
    mainLocationId: 0,
    mainPositionId: 0,
    payrollDepartmentId: 0,
    employmentType: '',
    payrollEmploymentTypeId: undefined,
    isManager: false,
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchFilterOptionsIfNeeded = async () => {
    if (filterOptionsLoaded || filterOptionsLoading) return;
    setFilterOptionsLoading(true);
    try {
      const [options, employmentTypesData] = await Promise.all([
        employeeApi.getListPageFilterOptions(),
        payrollEmploymentTypesApi.list(),
      ]);
      setLocations(options.locations.map((loc) => ({
        id: loc.id,
        name: loc.name,
        address: '',
        timezone: '',
        isActive: true,
        createdAt: '',
      })));
      setPositions(options.positions.map((pos) => ({
        id: pos.id,
        title: pos.title,
        color: '#3b82f6',
        isActive: true,
        createdAt: '',
      })));
      setPayrollDepartments(options.payrollDepartments.map((dept) => ({
        id: dept.id,
        name: dept.name,
        isActive: dept.isActive,
        createdAt: '',
      })));
      setSchedules(options.schedules.map((sch) => ({
        id: sch.id,
        name: sch.name,
        isActive: true,
        createdAt: '',
      })));
      setRoles(options.roles.map((role) => ({
        id: role.id,
        name: role.name,
        permissions: [],
        isActive: true,
        displayOrder: 0,
        createdAt: '',
      })));
      setPaymentTypes(options.paymentTypes.map((pt) => ({
        id: pt.id,
        name: pt.name,
        isActive: pt.isActive,
        createdAt: '',
      })));
      setPaymentFrequencies(options.paymentFrequencies.map((pf) => ({
        id: pf.id,
        name: pf.name,
        isActive: pf.isActive,
        createdAt: '',
      })));
      setLeavePolicies(options.leavePolicies.map((lp) => ({
        leavePolicyId: lp.leavePolicyId,
        policyName: lp.policyName,
        isActive: lp.isActive,
        isDefault: false,
        leaveYearStartMonth: 1,
        leaveYearStartDay: 1,
        allowCarryForward: false,
        maxCarryForwardDays: 0,
        minNoticeRequiredDays: 0,
        accruesDuringNoticePeriod: false,
        payOutOnTermination: false,
        createdAt: '',
        updatedAt: '',
      })));
      setPayrollEmploymentTypes(employmentTypesData);
      setFilterOptionsLoaded(true);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  const loadListPage = async (initialLoad = false) => {
    const requestId = ++listRequestRef.current;
    if (initialLoad) {
      setLoading(true);
    } else {
      setListRefreshing(true);
    }
    try {
      const activeFilters = filters.filter((f) => {
        if (!f.field) return false;
        if (f.valueFrom || f.valueTo) return true;
        if (Array.isArray(f.value) && f.value.length > 0) return true;
        return f.value !== '' && f.value !== null && f.value !== undefined;
      });

      const result = await employeeApi.queryListPage({
        status: statusFilter,
        locationId: selectedLocationId,
        q: searchTerm.trim() || undefined,
        sortColumn,
        sortDirection,
        presignImages: true,
        filters: activeFilters.map(({ field, value, valueFrom, valueTo }) => ({
          field,
          value,
          valueFrom,
          valueTo,
        })),
      });

      if (requestId !== listRequestRef.current) return;
      setEmployees(result.items);
      setTotalCount(result.totalCount);
    } catch (error) {
      if (requestId === listRequestRef.current) {
        console.error('Error loading employee list:', error);
      }
    } finally {
      if (requestId === listRequestRef.current) {
        setLoading(false);
        setListRefreshing(false);
      }
    }
  };

  useEffect(() => {
    const debounceMs = searchTerm.trim() ? 300 : 0;
    const timer = window.setTimeout(() => {
      void loadListPage(employees.length === 0 && loading);
    }, debounceMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, statusFilter, searchTerm, filters, sortColumn, sortDirection]);

  useEffect(() => {
    if (showAdvancedFilter || showModal) {
      void fetchFilterOptionsIfNeeded();
    }
  }, [showAdvancedFilter, showModal]);

  const handleAddNew = () => {
    void fetchFilterOptionsIfNeeded();
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      pin: '',
      appRoleIds: [],
      isActive: true,
      mainLocationId: 0,
      mainPositionId: 0,
      payrollDepartmentId: 0,
      employmentType: '',
      payrollEmploymentTypeId: undefined,
    });
    setConfirmPassword('');
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (employee: Employee) => {
    // Navigate to employee profile page for editing
    navigate(`/employees/${employee.id}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!formData.pin || formData.pin.length !== 4) {
      setError('PIN must be exactly 4 digits');
      return;
    }
    if (!formData.appRoleIds || formData.appRoleIds.length === 0) {
      setError('At least one app role is required');
      return;
    }
    if (!formData.mainLocationId || formData.mainLocationId === 0) {
      setError('Main Location is required');
      return;
    }
    if (!formData.mainPositionId || formData.mainPositionId === 0) {
      setError('Main Position is required');
      return;
    }
    if (!formData.payrollDepartmentId || formData.payrollDepartmentId === 0) {
      setError('Payroll Department is required');
      return;
    }
    const hasCatalogEt =
      formData.payrollEmploymentTypeId != null && formData.payrollEmploymentTypeId > 0;
    const hasCustomEt = (formData.employmentType || '').trim().length > 0;
    if (!hasCatalogEt && !hasCustomEt) {
      setError('Employment classification is required (choose a payroll type or Other / custom).');
      return;
    }

    try {
      const createPayload: CreateEmployeeRequest = { ...formData };
      if (hasCatalogEt) {
        delete createPayload.employmentType;
      } else {
        delete createPayload.payrollEmploymentTypeId;
      }
      const newEmployee = await employeeApi.create(createPayload);
      setShowModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        pin: '',
        appRoleIds: [],
        isActive: true,
        mainLocationId: 0,
        mainPositionId: 0,
        payrollDepartmentId: 0,
        employmentType: '',
        payrollEmploymentTypeId: undefined,
        isManager: false,
      });
      setConfirmPassword('');
      // Navigate to the new employee's profile page
      navigate(`/employees/${newEmployee.id}?tab=personal`);
    } catch (error: any) {
      console.error('Error creating employee:', error);
      setError(error?.response?.data?.message || 'Failed to create employee. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (await appConfirm('Are you sure you want to delete this employee?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await employeeApi.delete(id);
        void loadListPage();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee. Please try again.');
      }
    }
  };

  // Define filter field options
  const filterFields: FilterFieldOption[] = useMemo(() => {
    const fields: FilterFieldOption[] = [
      // Login tab fields - Only Email and PIN
      { value: 'email', label: 'Email', type: 'text', tab: 'Login' },
      { value: 'pin', label: 'PIN', type: 'text', tab: 'Login' },
      
      // Personal tab fields
      {
        value: 'title',
        label: 'Title',
        type: 'select',
        tab: 'Personal',
        options: [
          { value: '', label: 'Any' },
          { value: 'Mr', label: 'Mr' },
          { value: 'Mrs', label: 'Mrs' },
          { value: 'Ms', label: 'Ms' },
          { value: 'Miss', label: 'Miss' },
          { value: 'Mx', label: 'Mx' },
          { value: 'Dr', label: 'Dr' },
          { value: 'Prof', label: 'Prof' },
        ],
      },
      { value: 'firstName', label: 'First Name', type: 'text', tab: 'Personal' },
      { value: 'lastName', label: 'Last Name', type: 'text', tab: 'Personal' },
      { value: 'gender', label: 'Gender', type: 'select', tab: 'Personal', options: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Prefer Not to Say', label: 'Prefer Not to Say' }
      ]},
      { value: 'genderOther', label: 'Gender (Other)', type: 'text', tab: 'Personal' },
      { value: 'birthday', label: 'Birthday', type: 'dateRange', tab: 'Personal' },
      { value: 'phone', label: 'Phone', type: 'text', tab: 'Personal' },
      { value: 'address', label: 'Address (legacy)', type: 'text', tab: 'Personal' },
      { value: 'address1', label: 'Address line 1', type: 'text', tab: 'Personal' },
      { value: 'address2', label: 'Address line 2', type: 'text', tab: 'Personal' },
      { value: 'address3', label: 'Address line 3', type: 'text', tab: 'Personal' },
      { value: 'county', label: 'County', type: 'text', tab: 'Personal' },
      { value: 'zipCode', label: 'Zip code', type: 'text', tab: 'Personal' },
      { value: 'psaLicense', label: 'PSA License', type: 'text', tab: 'Personal' },
      { value: 'startDate', label: 'Start Date', type: 'dateRange', tab: 'Personal' },
      { value: 'visaNumber', label: 'Visa Number', type: 'text', tab: 'Personal' },
      { value: 'visaExpiryDate', label: 'Visa Expiry Date', type: 'dateRange', tab: 'Personal' },
      { value: 'comment', label: 'Comment', type: 'text', tab: 'Personal' },
      
      // Work tab fields
      { value: 'mainLocationId', label: 'Main Location', type: 'select', tab: 'Work', options: locations.map(loc => ({ value: loc.id, label: loc.name })) },
      { value: 'mainOrSubLocationIds', label: 'Main or Sub Locations', type: 'multiSelect', tab: 'Work', options: locations.map(loc => ({ value: loc.id, label: loc.name })) },
      { value: 'mainPositionId', label: 'Main Position', type: 'select', tab: 'Work', options: positions.map(pos => ({ value: pos.id, label: pos.title })) },
      { value: 'mainOrSubPositionIds', label: 'Main or Sub Positions', type: 'multiSelect', tab: 'Work', options: positions.map(pos => ({ value: pos.id, label: pos.title })) },
      { value: 'scheduleIds', label: 'Schedules', type: 'multiSelect', tab: 'Work', options: schedules.map(sch => ({ value: sch.id, label: sch.name })) },
      { value: 'appRoleIds', label: 'App Roles', type: 'multiSelect', tab: 'Work', options: roles.map(role => ({ value: role.id, label: role.name })) },
      { value: 'payrollDepartmentId', label: 'Payroll Department', type: 'select', tab: 'Work', options: payrollDepartments.filter(dept => dept.isActive).map(dept => ({ value: dept.id, label: dept.name })) },
      { value: 'employmentType', label: 'Employment Type', type: 'select', tab: 'Work', options: [
        { value: 'Permanent', label: 'Permanent' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Agency', label: 'Agency' }
      ]},
      { value: 'allowMobileClockIn', label: 'Allow Mobile Clock In', type: 'boolean', tab: 'Work' },
      {
        value: 'leavePolicyId',
        label: 'Assigned Leave Policy',
        type: 'select',
        tab: 'Leave',
        options: [
          { value: '', label: 'Any' },
          ...leavePolicies.map((lp) => ({
            value: lp.leavePolicyId,
            label: lp.isActive ? lp.policyName : `${lp.policyName} (inactive)`,
          })),
        ],
      },
      
      // License fields
      { value: 'licenseNumber', label: 'License - Number', type: 'text', tab: 'License' },
      { value: 'licenseExpiryDate', label: 'License - Expiry Date', type: 'dateRange', tab: 'License' },
      
      // Payroll fields
      { value: 'worksNumber', label: 'Works Number', type: 'text', tab: 'Payroll' },
      { value: 'ppsNumber', label: 'PPS Number', type: 'text', tab: 'Payroll' },
      { value: 'accountNumber', label: 'Bank Account Number', type: 'text', tab: 'Payroll' },
      { value: 'accountName', label: 'Bank Account Name', type: 'text', tab: 'Payroll' },
      { value: 'bankName', label: 'Bank Name', type: 'text', tab: 'Payroll' },
      { value: 'ban', label: 'BAN', type: 'text', tab: 'Payroll' },
      { value: 'bic', label: 'BIC', type: 'text', tab: 'Payroll' },
      { value: 'sortCode', label: 'Sort Code', type: 'text', tab: 'Payroll' },
      
      // Pay Record fields (only active records)
      { value: 'payRecordPaymentTypeId', label: 'Pay Type', type: 'select', tab: 'Pay Record', options: paymentTypes.filter(pt => pt.isActive).map(pt => ({ value: pt.id, label: pt.name })) },
      { value: 'payRecordPaymentFrequencyId', label: 'Pay Frequency', type: 'select', tab: 'Pay Record', options: paymentFrequencies.filter(pf => pf.isActive).map(pf => ({ value: pf.id, label: pf.name })) },
      { value: 'payRecordContractedHours', label: 'Contracted Hours (Per Week)', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordBaseRate', label: 'Pay Rate', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordSundayRateMultiplier', label: 'Sunday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordBankHolidayRateMultiplier', label: 'Bank Holiday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordNightShiftMultiplier', label: 'Night Shift Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOvertimeEligible', label: 'Is Overtime Eligible', type: 'boolean', tab: 'Pay Record' },
      { value: 'payRecordOt1TriggerHours', label: 'OT1 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt1RateMultiplier', label: 'OT1 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt2TriggerHours', label: 'OT2 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt2RateMultiplier', label: 'OT2 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      
      // Feedback fields
      { value: 'feedbackType', label: 'Feedback - Type', type: 'text', tab: 'Feedback' },
      { value: 'feedbackActionTaken', label: 'Feedback - Action Taken', type: 'text', tab: 'Feedback' },
      { value: 'feedbackDescription', label: 'Feedback - Description', type: 'text', tab: 'Feedback' },
      { value: 'feedbackPriority', label: 'Feedback - Priority', type: 'text', tab: 'Feedback' },
      { value: 'feedbackStatus', label: 'Feedback - Status', type: 'text', tab: 'Feedback' },
      
      // Training fields
      { value: 'trainingUnassigned', label: 'Training - Unassigned', type: 'boolean', tab: 'Training' },
      { value: 'trainingPending', label: 'Training - Pending', type: 'boolean', tab: 'Training' },
      { value: 'trainingCompleted', label: 'Training - Completed', type: 'boolean', tab: 'Training' },
      
      // Notes fields
      { value: 'notesSubject', label: 'Notes - Subject', type: 'text', tab: 'Notes' },
      { value: 'notesContent', label: 'Notes - Description', type: 'text', tab: 'Notes' },
    ];
    return fields;
  }, [locations, positions, payrollDepartments, schedules, roles, paymentTypes, paymentFrequencies, leavePolicies]);

  // Add a new filter condition
  const handleAddFilter = () => {
    const newFilter: FilterCondition = {
      id: Date.now().toString(),
      field: '',
      value: ''
    };
    setFilters([...filters, newFilter]);
  };

  // Remove a filter condition
  const handleRemoveFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  // Update a filter condition
  const handleFilterChange = (id: string, field: keyof FilterCondition, value: string | number | boolean | number[] | string[] | null | undefined) => {
    setFilters(prevFilters => prevFilters.map(f => {
      if (f.id === id) {
        return { ...f, [field]: value };
      }
      return f;
    }));
  };

  // Update filter field and reset value
  const handleFilterFieldChange = (id: string, fieldValue: string) => {
    setFilters(prevFilters => prevFilters.map(f => {
      if (f.id === id) {
        return { ...f, field: fieldValue, value: '', valueFrom: undefined, valueTo: undefined };
      }
      return f;
    }));
  };

  // Clear all filters and reset to show all employees
  const handleClearFilters = () => {
    setFilters([]);
  };

  // Handle sub-locations dialog
  const handleShowSubLocations = (employee: Employee) => {
    void fetchFilterOptionsIfNeeded();
    setSelectedEmployeeForSubLocations(employee);
    setShowSubLocationsDialog(true);
  };

  // Handle sub-positions dialog
  const handleShowSubPositions = (employee: Employee) => {
    void fetchFilterOptionsIfNeeded();
    setSelectedEmployeeForSubPositions(employee);
    setShowSubPositionsDialog(true);
  };

  // Get sub-locations for an employee
  const getSubLocations = (employee: Employee): Location[] => {
    if (!employee.profile?.subLocationIds || !Array.isArray(employee.profile.subLocationIds)) {
      return [];
    }
    return locations.filter(loc => employee.profile?.subLocationIds?.includes(loc.id));
  };

  // Handle column sorting
  const handleSort = (column: 'name' | 'position' | 'location') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sub-positions for an employee
  const getSubPositions = (employee: Employee): Position[] => {
    // Check if subPositionIds exists and is an array
    const subPositionIds = employee.profile?.subPositionIds;
    
    // Handle different data formats (array, string array, etc.)
    let positionIds: number[] = [];
    
    if (Array.isArray(subPositionIds)) {
      // Convert to numbers if they're strings
      positionIds = subPositionIds.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
    } else if (subPositionIds) {
      // If it's a single value or string, try to parse it
      const parsed = typeof subPositionIds === 'string' ? JSON.parse(subPositionIds) : subPositionIds;
      if (Array.isArray(parsed)) {
        positionIds = parsed.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
      }
    }
    
    if (positionIds.length === 0) {
      return [];
    }
    
    return positions.filter(pos => positionIds.includes(pos.id));
  };

  // Get field type for a filter field
  const getFieldType = (fieldValue: string): FilterFieldType => {
    const field = filterFields.find(f => f.value === fieldValue);
    return field?.type || 'text';
  };

  // Get field options for a filter field
  const getFieldOptions = (fieldValue: string) => {
    const field = filterFields.find(f => f.value === fieldValue);
    return field?.options || [];
  };


  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[82%] mx-auto p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">Employees</h1>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-neutral-600">
              Showing <span className="font-semibold text-neutral-800">{employees.length}</span> of <span className="font-semibold text-neutral-800">{totalCount}</span> {totalCount === 1 ? 'employee' : 'employees'}
              {listRefreshing && (
                <Loader2 className="inline h-3.5 w-3.5 ml-2 animate-spin text-neutral-400" />
              )}
              {statusFilter === 'Active' && (
                <span className="text-neutral-500 ml-1">
                  (Active only)
                </span>
              )}
              {statusFilter === 'InActive' && (
                <span className="text-neutral-500 ml-1">
                  (Inactive only)
                </span>
              )}
            </span>
            {(selectedLocationId || statusFilter !== 'Active' || searchTerm || filters.length > 0) && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                Filtered
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/employees/organization-chart"
            className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-md hover:bg-violet-700 transition-colors flex items-center gap-2"
          >
            <Building2 className="h-4 w-4" />
            Organization chart
          </Link>
          <button
            onClick={async () => {
              setShowManagerAssignmentModal(true);
              setManagerAssignmentLoading(true);
              try {
                const [locs, pickerRows] = await Promise.all([
                  locationApi.getAll(),
                  employeeApi.getPickerAsEmployees(),
                ]);
                setLocationsWithManagers(locs);
                setManagerPickerEmployees(pickerRows);
              } catch (err) {
                console.error('Failed to load locations:', err);
              } finally {
                setManagerAssignmentLoading(false);
              }
            }}
            className="px-4 py-2 bg-neutral-600 text-white text-sm font-medium rounded-md hover:bg-neutral-700 transition-colors flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            Manager Assignment
          </button>
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Employee
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search employees by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Advanced Filter Section and Status Filter */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <button
            onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors w-full sm:w-auto"
          >
            <Filter className="h-4 w-4" />
            Advanced Filters
            {filters.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                {filters.length}
              </span>
            )}
            {showAdvancedFilter ? (
              <ChevronUp className="h-4 w-4 ml-auto" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-auto" />
            )}
          </button>

          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-neutral-700 whitespace-nowrap">
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'Active' | 'InActive' | 'All')}
              className="px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <option value="Active">Active</option>
              <option value="InActive">InActive</option>
              <option value="All">All</option>
            </select>
          </div>
        </div>

        {/* Advanced Filter Content */}
        {showAdvancedFilter && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-neutral-800">Filter Employees</h3>
              <div className="flex gap-2">
                {filters.length > 0 && (
                  <>
                    <button
                      onClick={handleClearFilters}
                      className="text-xs text-neutral-600 hover:text-neutral-800 px-2 py-1"
                    >
                      Clear All
                    </button>
                  </>
                )}
                <button
                  onClick={handleAddFilter}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
                >
                  <Plus className="h-3 w-3" />
                  Add Filter
                </button>
              </div>
            </div>

            {filterOptionsLoading && (
              <p className="text-xs text-neutral-500 mb-3 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading filter options...
              </p>
            )}

            {filters.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-4">
                No filters applied. Click "Add Filter" to start filtering employees.
              </p>
            ) : (
              <div className="space-y-3">
                {filters.map((filter) => {
                  const fieldType = getFieldType(filter.field);
                  const fieldOptions = getFieldOptions(filter.field);
                  const selectedField = filterFields.find(f => f.value === filter.field);

                  // Convert filterFields to react-select options
                  type FieldSelectOption = { value: string; label: string };
                  const fieldSelectOptions: FieldSelectOption[] = filterFields.map(field => ({
                    value: field.value,
                    label: field.label
                  }));

                  const selectedFieldOption: FieldSelectOption | null = filter.field 
                    ? fieldSelectOptions.find(opt => opt.value === filter.field) || null
                    : null;

                  // Custom styles for react-select (similar to BasketSelect but for single select)
                  const fieldSelectStyles: StylesConfig<FieldSelectOption, false> = {
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      minHeight: '38px',
                      fontSize: '14px',
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#9ca3af',
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: '#374151',
                    }),
                  };

                  return (
                    <div key={filter.id} className="flex flex-col sm:flex-row gap-2 p-3 bg-neutral-50 rounded-md">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-neutral-700 mb-1">
                          Field
                        </label>
                        <Select<FieldSelectOption>
                          options={fieldSelectOptions}
                          value={selectedFieldOption}
                          onChange={(option: SingleValue<FieldSelectOption>) => {
                            handleFilterFieldChange(filter.id, option ? option.value : '');
                          }}
                          placeholder="Select Field..."
                          styles={fieldSelectStyles}
                          isSearchable
                          isClearable
                          className="react-select-container"
                          classNamePrefix="react-select"
                          noOptionsMessage={({ inputValue }) => 
                            inputValue ? `No fields found for "${inputValue}"` : 'No fields available'
                          }
                        />
                      </div>

                      <div className="flex-1">
                        {fieldType !== 'dateRange' && fieldType !== 'numberRange' && (
                          <label className="block text-xs font-medium text-neutral-700 mb-1">
                            Value
                          </label>
                        )}
                        {fieldType === 'boolean' ? (
                          <select
                            value={String(filter.value || '')}
                            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value === 'true')}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : fieldType === 'dateRange' ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                              <input
                                type="date"
                                value={String(filter.valueFrom || '')}
                                onChange={(e) => handleFilterChange(filter.id, 'valueFrom', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                              <input
                                type="date"
                                value={String(filter.valueTo || '')}
                                onChange={(e) => handleFilterChange(filter.id, 'valueTo', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ) : fieldType === 'numberRange' ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                              <input
                                type="number"
                                step="0.01"
                                value={filter.valueFrom !== null && filter.valueFrom !== undefined ? filter.valueFrom : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleFilterChange(filter.id, 'valueFrom', val === '' ? null : parseFloat(val));
                                }}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                              <input
                                type="number"
                                step="0.01"
                                value={filter.valueTo !== null && filter.valueTo !== undefined ? filter.valueTo : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleFilterChange(filter.id, 'valueTo', val === '' ? null : parseFloat(val));
                                }}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ) : fieldType === 'multiSelect' && fieldOptions.length > 0 ? (
                          <BasketSelect
                            label=""
                            availableItems={fieldOptions.map(opt => ({ id: opt.value, label: opt.label }))}
                            selectedIds={(() => {
                              if (Array.isArray(filter.value)) {
                                // Handle array case - filter out booleans and keep only strings/numbers
                                const arr = filter.value as (string | number | boolean)[];
                                return arr.filter((v): v is string | number => 
                                  typeof v === 'string' || typeof v === 'number'
                                );
                              }
                              if (filter.value && (typeof filter.value === 'string' || typeof filter.value === 'number')) {
                                return [filter.value];
                              }
                              return [];
                            })()}
                            onChange={(selectedIds) => {
                              // Preserve the original type from options (number or string)
                              // Check if first option value is a number to determine type
                              const isNumeric = fieldOptions.length > 0 && typeof fieldOptions[0].value === 'number';
                              
                              if (isNumeric) {
                                const numValues: number[] = selectedIds.map(id => {
                                  const option = fieldOptions.find(opt => String(opt.value) === String(id));
                                  return option && typeof option.value === 'number' ? option.value : Number(id);
                                });
                                handleFilterChange(filter.id, 'value', numValues);
                              } else {
                                const strValues: string[] = selectedIds.map(id => {
                                  const option = fieldOptions.find(opt => String(opt.value) === String(id));
                                  return option ? String(option.value) : String(id);
                                });
                                handleFilterChange(filter.id, 'value', strValues);
                              }
                            }}
                            placeholder={`Select ${selectedField?.label.toLowerCase() || 'options'}...`}
                          />
                        ) : fieldType === 'select' && fieldOptions.length > 0 ? (
                          <select
                            value={String(filter.value || '')}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (!selectedValue) {
                                handleFilterChange(filter.id, 'value', '');
                                return;
                              }
                              const selectedOption = fieldOptions.find(opt => String(opt.value) === selectedValue);
                              if (selectedOption) {
                                // Preserve the original type (number or string)
                                handleFilterChange(filter.id, 'value', selectedOption.value);
                              } else {
                                // Fallback: try to convert to number if it looks like a number
                                const numValue = Number(selectedValue);
                                handleFilterChange(filter.id, 'value', isNaN(numValue) ? selectedValue : numValue);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {fieldOptions.map((option) => (
                              <option key={String(option.value)} value={String(option.value)}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={String(filter.value || '')}
                            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                            placeholder={`Enter ${selectedField?.label.toLowerCase() || 'value'}...`}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={() => handleRemoveFilter(filter.id)}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove filter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Employee Table - Desktop */}
      <div className="hidden md:block bg-white border border-neutral-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700 w-12"></th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    {sortColumn === 'name' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Phone</th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('position')}
                >
                  <div className="flex items-center gap-2">
                    Position
                    {sortColumn === 'position' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('location')}
                >
                  <div className="flex items-center gap-2">
                    Location
                    {sortColumn === 'location' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 px-4 text-center text-neutral-600">
                    {searchTerm || filters.length > 0 ? 'No employees found matching your filters.' : 'No employees found.'}
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <tr key={employee.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                    <td className="py-3 px-4">
                      {employee.profileImageUrl ? (
                        <img
                          src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                          alt={`${employee.firstName} ${employee.lastName}`}
                          className="w-10 h-10 rounded-full object-cover border border-neutral-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-neutral-500" />
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-neutral-800">
                      {employee.firstName} {employee.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      {employee.email}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      {employee.phone || employee.phoneNumber || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {employee.profile?.mainPosition ? (
                        <div className="inline-flex items-center gap-1">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: employee.profile.mainPosition.color || '#3b82f6' }}>
                            {employee.profile.mainPosition.title}
                          </span>
                          {getSubPositions(employee).length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowSubPositions(employee);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                              title={`${getSubPositions(employee).length} additional position${getSubPositions(employee).length > 1 ? 's' : ''}`}
                            >
                              +{getSubPositions(employee).length}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      <div className="inline-flex items-center gap-1">
                        <span>{employee.profile?.mainLocation?.name || employee.profile?.locationName || '-'}</span>
                        {getSubLocations(employee).length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowSubLocations(employee);
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                            title={`${getSubLocations(employee).length} additional location${getSubLocations(employee).length > 1 ? 's' : ''}`}
                          >
                            +{getSubLocations(employee).length}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {employee.isActive ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => setHierarchyEmployeeId(employee.id)}
                          className="p-2 text-neutral-600 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
                          title="Reporting hierarchy"
                        >
                          <Network className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(employee)}
                          className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit employee"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(employee.id)}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete employee"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {employees.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
            <User className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <p className="text-neutral-600">
              {searchTerm || filters.length > 0 ? 'No employees found matching your filters.' : 'No employees found.'}
            </p>
          </div>
        ) : (
          employees.map((employee) => (
            <div key={employee.id} className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {employee.profileImageUrl ? (
                    <img
                      src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                      alt={`${employee.firstName} ${employee.lastName}`}
                      className="w-12 h-12 rounded-full object-cover border border-neutral-200 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-neutral-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-medium text-neutral-900">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1 truncate">{employee.email}</p>
                    {employee.phone || employee.phoneNumber ? (
                      <p className="text-sm text-neutral-600">{employee.phone || employee.phoneNumber}</p>
                    ) : null}
                  </div>
                </div>
                {employee.isActive ? (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded flex-shrink-0">Active</span>
                ) : (
                  <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded flex-shrink-0">Inactive</span>
                )}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {employee.profile?.mainPosition && (
                  <div className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: employee.profile.mainPosition.color || '#3b82f6' }}>
                      {employee.profile.mainPosition.title}
                    </span>
                    {getSubPositions(employee).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowSubPositions(employee);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                        title={`${getSubPositions(employee).length} additional position${getSubPositions(employee).length > 1 ? 's' : ''}`}
                      >
                        +{getSubPositions(employee).length}
                      </button>
                    )}
                  </div>
                )}
                {employee.profile?.mainLocation && (
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-neutral-600">
                      {employee.profile.mainLocation.name}
                    </span>
                    {getSubLocations(employee).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowSubLocations(employee);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                        title={`${getSubLocations(employee).length} additional location${getSubLocations(employee).length > 1 ? 's' : ''}`}
                      >
                        +{getSubLocations(employee).length}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end mt-3">
                <button
                  type="button"
                  onClick={() => setHierarchyEmployeeId(employee.id)}
                  className="p-2 text-neutral-600 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
                  title="Reporting hierarchy"
                >
                  <Network className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleEdit(employee)}
                  className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title="Edit employee"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(employee.id)}
                  className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Delete employee"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Employee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-800 mb-6">
              Add New Employee
            </h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm password"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  PIN (4 digits) *
                </label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setFormData({ ...formData, pin: value });
                  }}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter 4-digit PIN"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Main Location *
                  </label>
                  <select
                    required
                    value={formData.mainLocationId || ''}
                    onChange={(e) => setFormData({ ...formData, mainLocationId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Main Position *
                  </label>
                  <select
                    required
                    value={formData.mainPositionId || ''}
                    onChange={(e) => setFormData({ ...formData, mainPositionId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Position</option>
                    {positions.map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employee or Manager</label>
                <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isManager: false })}
                    className={`px-4 py-2 text-sm font-medium ${
                      !formData.isManager
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    Employee
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isManager: true })}
                    className={`px-4 py-2 text-sm font-medium border-l border-neutral-300 ${
                      formData.isManager
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    Manager
                  </button>
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                  Only Managers can later be assigned as a line manager or location manager.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Payroll Department *
                  </label>
                  <select
                    required
                    value={formData.payrollDepartmentId || ''}
                    onChange={(e) => setFormData({ ...formData, payrollDepartmentId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Payroll Department</option>
                    {payrollDepartments.filter(dept => dept.isActive).map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Employment classification *
                  </label>
                  <select
                    required
                    value={
                      formData.payrollEmploymentTypeId != null && formData.payrollEmploymentTypeId > 0
                        ? String(formData.payrollEmploymentTypeId)
                        : 'other'
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'other') {
                        setFormData((prev) => ({ ...prev, payrollEmploymentTypeId: undefined }));
                        return;
                      }
                      const id = parseInt(v, 10);
                      const row = payrollEmploymentTypes.find((t) => t.id === id);
                      setFormData((prev) => ({
                        ...prev,
                        payrollEmploymentTypeId: id,
                        employmentType: row?.displayName ?? prev.employmentType,
                      }));
                    }}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" disabled>
                      {payrollEmploymentTypes.length ? 'Select payroll employment type' : 'Loading types…'}
                    </option>
                    {payrollEmploymentTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.displayName}
                        {t.code ? ` (${t.code})` : ''}
                      </option>
                    ))}
                    <option value="other">Other (custom label)</option>
                  </select>
                  {(formData.payrollEmploymentTypeId == null || formData.payrollEmploymentTypeId <= 0) && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Custom employment label</label>
                      <input
                        type="text"
                        value={formData.employmentType || ''}
                        onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                        placeholder="e.g. legacy or non-catalog text"
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              <BasketSelect
                label="App Roles"
                required
                availableItems={roles.map(role => ({ id: role.id, label: role.name }))}
                selectedIds={formData.appRoleIds}
                onChange={(ids) => setFormData({ ...formData, appRoleIds: ids.map(id => typeof id === 'string' ? parseInt(id) : id) })}
                placeholder="Select roles..."
              />

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setError(null);
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Create Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manager Assignment Modal */}
      {showManagerAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h2 className="text-xl font-semibold text-neutral-800">Location manager</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Default manager for people at this location who have no line manager. Only people marked as
                  Manager on their profile can be assigned here.
                </p>
              </div>
              <button
                onClick={() => setShowManagerAssignmentModal(false)}
                className="text-neutral-400 hover:text-neutral-600 shrink-0"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {managerAssignmentLoading ? (
              <div className="py-8 text-center text-neutral-500">Loading locations...</div>
            ) : (
              <div className="space-y-4">
                {locationsWithManagers.map((loc) => (
                  <ManagerAssignmentRow
                    key={loc.id}
                    location={loc}
                    employees={managerPickerEmployees.filter(
                      (e) => e.profile?.isManager || e.id === loc.managerId
                    )}
                    onManagerChange={async (employeeId) => {
                      setManagerAssignmentSaving(prev => ({ ...prev, [loc.id]: true }));
                      try {
                        await locationApi.setManager(loc.id, employeeId);
                        setLocationsWithManagers(prev =>
                          prev.map(l =>
                            l.id === loc.id
                              ? {
                                  ...l,
                                  managerId: employeeId ?? undefined,
                                  manager: employeeId
                                    ? managerPickerEmployees.find(e => e.id === employeeId) ?? l.manager
                                    : undefined
                                }
                              : l
                          )
                        );
                      } catch (err: any) {
                        console.error('Failed to set manager:', err);
                        const data = err?.response?.data;
                        const msg =
                          typeof data === 'string'
                            ? data
                            : data?.message || 'Failed to set location manager.';
                        window.alert(msg);
                      } finally {
                        setManagerAssignmentSaving(prev => ({ ...prev, [loc.id]: false }));
                      }
                    }}
                    saving={managerAssignmentSaving[loc.id]}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-Locations Dialog */}
      {showSubLocationsDialog && selectedEmployeeForSubLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Additional Locations for {selectedEmployeeForSubLocations?.firstName} {selectedEmployeeForSubLocations?.lastName}
              </h3>
              <button
                onClick={() => {
                  setShowSubLocationsDialog(false);
                  setSelectedEmployeeForSubLocations(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {selectedEmployeeForSubLocations && getSubLocations(selectedEmployeeForSubLocations).length === 0 ? (
                <p className="text-neutral-500 text-center py-4">No additional locations</p>
              ) : selectedEmployeeForSubLocations ? (
                <ul className="space-y-2">
                  {getSubLocations(selectedEmployeeForSubLocations).map((location) => (
                    <li key={location.id} className="px-3 py-2 bg-neutral-50 rounded-md text-sm text-neutral-700">
                      {location.name}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowSubLocationsDialog(false);
                  setSelectedEmployeeForSubLocations(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Positions Dialog */}
      {showSubPositionsDialog && selectedEmployeeForSubPositions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Additional Positions for {selectedEmployeeForSubPositions?.firstName} {selectedEmployeeForSubPositions?.lastName}
              </h3>
              <button
                onClick={() => {
                  setShowSubPositionsDialog(false);
                  setSelectedEmployeeForSubPositions(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {selectedEmployeeForSubPositions && getSubPositions(selectedEmployeeForSubPositions).length === 0 ? (
                <p className="text-neutral-500 text-center py-4">No additional positions</p>
              ) : selectedEmployeeForSubPositions ? (
                <ul className="space-y-2">
                  {getSubPositions(selectedEmployeeForSubPositions).map((position) => (
                    <li key={position.id} className="px-3 py-2 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: position.color || '#3b82f6' }}>
                      {position.title}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowSubPositionsDialog(false);
                  setSelectedEmployeeForSubPositions(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {hierarchyEmployeeId !== null && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
              <Loader2 className="h-10 w-10 animate-spin text-violet-500" aria-label="Loading" />
            </div>
          }
        >
          <EmployeeHierarchyModal
            employeeId={hierarchyEmployeeId}
            onClose={() => setHierarchyEmployeeId(null)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default EmployeeList;
